import { useCallback, useEffect, useMemo, useRef } from "react";
import { EVENTS, STATUS, useJoyride } from "react-joyride";
import { TOUR_IDS } from "@demandproai/shared";
import { useEasyModeContext } from "../contexts/EasyModeContext";
import { useTourContext } from "../contexts/TourContext";
import { TOUR_OPTIONS } from "../constants/tour";
import { buildEasyModeTourSteps, getEasyModeTourStops } from "../tours/easyModeTourSteps";
import { evaluateRequirement, getCanSkipRequirement } from "../tours/tourRequirement";
import GatedTourTooltip from "../components/tour/GatedTourTooltip";
import useIsModalOpen from "./useIsModalOpen";
import useTourSpotlightSync from "./useTourSpotlightSync";
import useTourAnchorRecovery from "./useTourAnchorRecovery";

const useEasyModeTour = () => {
   const { step, stepValidation, goToStep, isCaseLoading } = useEasyModeContext();
   const {
      registerTour,
      requestedTourId,
      clearTourRequest,
      isTourPending,
      completeTour,
      dismissTour,
   } = useTourContext();
   const isModalOpen = useIsModalOpen();

   // Read through refs so the step hooks stay stable. goToStep is rebuilt on
   // every render of the wizard, and depending on it would rebuild the steps
   // array underneath a running tour
   const stepRef = useRef(step);
   const goToStepRef = useRef(goToStep);

   useEffect(() => {
      stepRef.current = step;
   }, [step]);

   useEffect(() => {
      goToStepRef.current = goToStep;
   }, [goToStep]);

   // The wizard refuses a forward jump over an unfinished step. That is the same
   // rule the gate enforces, so by the time Next is available the move is
   // allowed; a refusal just leaves the anchor absent and the stop is skipped
   const goToWizardStep = useCallback(async (targetStep) => {
      if (targetStep === stepRef.current) {
         return;
      }

      await goToStepRef.current({ step: targetStep });
   }, []);

   const steps = useMemo(() => buildEasyModeTourSteps({ goToWizardStep }), [goToWizardStep]);

   const shouldAutoRun = !isCaseLoading && isTourPending(TOUR_IDS.EASY_MODE);

   const handleTourEvent = useCallback(
      (data) => {
         if (data.type !== EVENTS.TOUR_END) {
            return;
         }

         if (data.status === STATUS.SKIPPED) {
            dismissTour(TOUR_IDS.EASY_MODE);
         } else {
            completeTour(TOUR_IDS.EASY_MODE);
         }

         clearTourRequest();
      },
      [completeTour, dismissTour, clearTourRequest]
   );

   const { Tour, controls, state, step: activeStep } = useJoyride({
      steps,
      run: shouldAutoRun,
      continuous: true,
      tooltipComponent: GatedTourTooltip,
      options: TOUR_OPTIONS,
      onEvent: handleTourEvent,
   });

   useTourSpotlightSync({ step: activeStep, state });
   useTourAnchorRecovery({ step: activeStep, state, controls });

   const requirement = getEasyModeTourStops()[state.index]?.requirement;

   const { isBlocked, hint } = evaluateRequirement({ requirement, stepValidation });

   const advance = useCallback(() => controls.next(), [controls]);

   const skipStop = getCanSkipRequirement(requirement) ? advance : null;

   const gate = useMemo(() => ({ isBlocked, hint, skipStop }), [isBlocked, hint, skipStop]);

   // Resuming is only correct for a tour that is mid run, so this is read rather
   // than depended on: a status change must not itself trigger a pause
   const isMidRunRef = useRef(false);
   isMidRunRef.current = [STATUS.RUNNING, STATUS.PAUSED].includes(state.status);

   // The partner import confirmation is an XModal, which renders below the tour
   // overlay. Pausing holds the index, unlike toggling run
   useEffect(() => {
      if (!isMidRunRef.current) {
         return;
      }

      if (isModalOpen) {
         controls.stop();
      } else {
         controls.start();
      }
   }, [isModalOpen, controls]);

   // A replay rewinds to the first stop rather than resuming where the user left off
   useEffect(() => {
      if (requestedTourId === TOUR_IDS.EASY_MODE) {
         controls.reset(true);
      }
   }, [requestedTourId, controls]);

   useEffect(() => registerTour(TOUR_IDS.EASY_MODE), [registerTour]);

   return { Tour, gate };
};

export default useEasyModeTour;
